import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// ── 시·도 정규화 (백엔드 hospital-matcher.js와 동일) ──
const SIDO_MAP = {
    '서울': '서울', '서울특별시': '서울', '서울시': '서울',
    '부산': '부산', '부산광역시': '부산', '부산시': '부산',
    '대구': '대구', '대구광역시': '대구',
    '인천': '인천', '인천광역시': '인천',
    '광주': '광주', '광주광역시': '광주',
    '대전': '대전', '대전광역시': '대전',
    '울산': '울산', '울산광역시': '울산',
    '세종': '세종', '세종특별자치시': '세종',
    '경기': '경기', '경기도': '경기',
    '강원': '강원', '강원도': '강원', '강원특별자치도': '강원',
    '충북': '충북', '충청북도': '충북',
    '충남': '충남', '충청남도': '충남',
    '전북': '전북', '전라북도': '전북', '전북특별자치도': '전북',
    '전남': '전남', '전라남도': '전남',
    '경북': '경북', '경상북도': '경북',
    '경남': '경남', '경상남도': '경남',
    '제주': '제주', '제주도': '제주', '제주특별자치도': '제주',
};

function normalizeSido(s) {
    if (!s) return '';
    return SIDO_MAP[s.trim()] || s.replace(/특별시|광역시|특별자치도|특별자치시|도$/g, '');
}

/**
 * Daum 우편번호 위젯 (모바일 친화 embed 방식)
 *
 * Props:
 *   onComplete(data) — 선택 완료 콜백, 다음 필드 전달:
 *     - 카카오 응답 그대로: postcode, address(=roadAddress), jibunAddress, sido, sigungu, bname, buildingName
 *     - 매칭 키 추가: _matchSido, _matchSggu, _matchDong
 *   onClose() — 닫기 버튼 클릭
 */
export default function AddressPicker({ onComplete, onClose }) {
    const containerRef = useRef(null);
    const initializedRef = useRef(false);

    useEffect(() => {
        if (initializedRef.current) return;
        let attempts = 0;
        const MAX_ATTEMPTS = 30; // 3초 (100ms × 30)
        const tryInit = () => {
            if (!window.daum?.Postcode || !containerRef.current) {
                attempts++;
                if (attempts >= MAX_ATTEMPTS) {
                    console.error('Daum Postcode not loaded');
                    alert('주소 검색 서비스를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.');
                    onClose?.();
                    return;
                }
                setTimeout(tryInit, 100);
                return;
            }
            initializedRef.current = true;
            // Daum Postcode v2는 embed 컨테이너의 실제 size를 사용
            // (width/height 옵션은 popup 모드용 — embed에선 컨테이너 크기로 동작)
            new window.daum.Postcode({
                oncomplete: (data) => {
                    const enriched = {
                        ...data,
                        roadAddress: data.address || '',          // 도로명
                        jibunAddress: data.jibunAddress || '',    // 지번
                        // 매칭 키 (백엔드 알고리즘과 일치)
                        _matchSido: normalizeSido(data.sido),
                        _matchSggu: data.sigungu || '',
                        _matchDong: data.bname || '',
                    };
                    onComplete?.(enriched);
                },
                onresize: (size) => {
                    if (containerRef.current && size?.height) {
                        containerRef.current.style.height = size.height + 'px';
                    }
                },
                autoResize: true,
            }).embed(containerRef.current, { autoClose: false });
        };
        tryInit();
    }, [onComplete]);

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                <div style={S.header}>
                    <h3 style={S.title}>주소 검색</h3>
                    <button onClick={onClose} style={S.closeBtn}><X size={20} /></button>
                </div>
                <p style={S.hint}>건물명·도로명·지번 중 아무거나 검색해주세요</p>
                <div ref={containerRef} style={S.embedArea} />
            </div>
        </div>
    );
}

const S = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 500,
        height: '90vh', maxHeight: 700,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0,
    },
    title: { margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' },
    closeBtn: {
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#64748b', padding: 4, display: 'flex',
    },
    hint: { fontSize: 11, color: '#94a3b8', margin: '8px 16px 0', flexShrink: 0 },
    embedArea: {
        flex: 1, minHeight: 0,
        width: '100%', height: '100%', overflow: 'auto',
    },
};
